import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { User, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { LoginHero } from "@/components/LoginHero";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [identifier, setId] = useState("");
  const [password, setPw] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [forgot, setForgot] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(identifier.trim(), password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.45fr_1fr] bg-slate-50">
      <LoginHero />
      <div className="flex items-center justify-center p-5 sm:p-10 bg-gradient-to-br from-slate-50 to-slate-100 order-1 lg:order-2">
        <form onSubmit={submit} className="w-full max-w-md bg-white rounded-3xl shadow-[0_20px_60px_-20px_rgba(7,20,49,0.25)] border border-slate-100 p-7 sm:p-10 space-y-6 fade-up" data-testid="login-form">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#C9971F] font-semibold">Ayzal Traders</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#071431] mt-2">Welcome Back</h2>
            <p className="text-base text-slate-500 mt-1">Sign in to your DBR workspace</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="identifier" className="font-semibold text-slate-800">Email or Mobile</Label>
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input id="identifier" value={identifier} onChange={(e) => setId(e.target.value)} required autoComplete="username" placeholder="Enter your email or mobile number" className="h-12 pl-11 rounded-xl" data-testid="login-identifier-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="font-semibold text-slate-800">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input id="password" type={show ? "text" : "password"} value={password} onChange={(e) => setPw(e.target.value)} required autoComplete="current-password" placeholder="Enter your password" className="h-12 pl-11 pr-11 rounded-xl" data-testid="login-password-input" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Toggle password" data-testid="login-toggle-password">
                {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <div className="text-right">
              <button type="button" onClick={() => setForgot((f) => !f)} className="text-sm font-semibold text-blue-700 hover:underline" data-testid="login-forgot-password">Forgot password?</button>
            </div>
            {forgot && <p className="text-xs text-slate-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2" data-testid="login-forgot-info">Password reset ke liye Admin se contact karein — Admin Users page se naya password set kar sakta hai.</p>}
          </div>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={busy} className="w-full h-12 rounded-xl bg-[#0B4FD6] hover:bg-[#0941b3] text-white text-base font-semibold transition-colors" data-testid="login-submit-btn">
            {busy ? "Signing in..." : <>Sign in <ArrowRight className="h-5 w-5" /></>}
          </Button>
          <p className="flex items-center justify-center gap-2 text-sm text-slate-500"><Lock className="h-4 w-4" /> Secure &amp; Encrypted Access</p>
        </form>
      </div>
    </div>
  );
}
